import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  emailTemplatesFetchAPI,
  emailTemplateCreateAPI,
  emailTemplateUpdateAPI,
  emailTemplateDeleteAPI,
} from "api/emailTemplate/emailTemplateApiHelper"
import Notify from "components/Common/notify"

// The list endpoint may return a bare array or a paginated envelope — handle both.
const extractItems = body => {
  if (!body) return []
  if (Array.isArray(body)) return body
  if (Array.isArray(body.items)) return body.items
  if (Array.isArray(body.data)) return body.data
  if (body.data && Array.isArray(body.data.items)) return body.data.items
  return []
}
const extractTotalPages = body => {
  if (!body) return 1
  return body.totalPages || body.data?.totalPages || 1
}

export const EmailTemplatesFetch = createAsyncThunk(
  "EmailTemplate/EmailTemplatesFetch",
  async data => {
    try {
      const response = await emailTemplatesFetchAPI(data)
      return {
        data: response?.data,
        status: response?.status,
      }
    } catch (error) {
      Notify("error", "Failed to fetch email templates")
      throw Error("Failed to fetch email templates")
    }
  },
)

export const EmailTemplateCreate = createAsyncThunk(
  "EmailTemplate/EmailTemplateCreate",
  async data => {
    try {
      const response = await emailTemplateCreateAPI(data)
      return {
        data: response?.data,
        status: response?.status,
      }
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to create email template"
      Notify("error", errorMsg)
      return error
    }
  },
)

export const EmailTemplateUpdate = createAsyncThunk(
  "EmailTemplate/EmailTemplateUpdate",
  async ({ id: id, data: data }) => {
    try {
      const response = await emailTemplateUpdateAPI({ id: id, data: data })
      return {
        data: response?.data,
        status: response?.status,
      }
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to update email template"
      Notify("error", errorMsg)
      return error
    }
  },
)

export const EmailTemplateDelete = createAsyncThunk(
  "EmailTemplate/EmailTemplateDelete",
  async id => {
    try {
      const response = await emailTemplateDeleteAPI(id)
      if (response?.status === 200 || response?.status === 204) {
        Notify("success", "Email template deleted.")
        return { id, status: response?.status }
      }
      Notify("error", "Failed to delete email template")
      throw Error("Failed to delete email template")
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to delete email template"
      Notify("error", errorMsg)
      throw Error("Failed to delete email template")
    }
  },
)

const initialState = {
  emailTemplatesData: [],
  totalPages: 1,
  loading: false,
  error: null,
}

export const EmailTemplateSlice = createSlice({
  name: "EmailTemplateSlice",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(EmailTemplatesFetch.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(EmailTemplatesFetch.fulfilled, (state, action) => {
        state.loading = false
        const body = action.payload?.data
        state.emailTemplatesData = extractItems(body)
        state.totalPages = extractTotalPages(body)
      })
      .addCase(EmailTemplatesFetch.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(EmailTemplateCreate.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(EmailTemplateCreate.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.status === 200 || action.payload?.status === 201) {
          const created =
            action.payload?.data?.data || action.payload?.data
          if (created) {
            state.emailTemplatesData = [...state.emailTemplatesData, created]
          }
        }
      })
      .addCase(EmailTemplateCreate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(EmailTemplateUpdate.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(EmailTemplateUpdate.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.status === 200) {
          const updated =
            action.payload?.data?.data || action.payload?.data
          if (updated?.id) {
            state.emailTemplatesData = state.emailTemplatesData.map(t =>
              t?.id === updated.id ? updated : t,
            )
          }
        }
      })
      .addCase(EmailTemplateUpdate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(EmailTemplateDelete.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(EmailTemplateDelete.fulfilled, (state, action) => {
        state.loading = false
        state.emailTemplatesData = state.emailTemplatesData.filter(
          t => t?.id !== action.payload?.id,
        )
      })
      .addCase(EmailTemplateDelete.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  },
})

export const allEmailTemplates = state => state.allEmailTemplates

export default EmailTemplateSlice.reducer
